/**
 * RatiosChart
 *
 * One stacked horizontal bar per matter, split by timekeeper. Segment length is
 * hours; the full bar is the matter's total for the period.
 *
 * Stacking is the point of the report — "individual timekeeper contributions to
 * matters" is a question about proportions within a matter, and side-by-side
 * bars make that comparison harder rather than easier.
 */

import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { RatioMatter } from "../../types/reports";

/**
 * Distinct timekeepers given their own colour before the rest are pooled.
 * Past this a legend stops being readable and colour stops carrying meaning.
 */
const MAX_SERIES = 8;

const SERIES_COLORS = [
	"var(--chart-1)",
	"var(--chart-2)",
	"var(--chart-3)",
	"var(--chart-5)",
	"var(--chart-4)",
	"var(--brand-600)",
	"var(--brand-800)",
	"var(--brand-mid-slate)",
];
const OTHER_COLOR = "var(--muted-foreground)";
const OTHER_KEY = "Other";

interface RatiosChartProps {
	matters: RatioMatter[];
	limit?: number;
}

function truncate(text: string, max: number): string {
	return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function ChartTooltip({
	active,
	payload,
	label,
}: {
	active?: boolean;
	payload?: Array<{ name: string; value: number; color: string }>;
	label?: string;
}) {
	if (!active || !payload?.length) return null;
	const shown = payload.filter((p) => p.value > 0);
	const total = shown.reduce((sum, p) => sum + p.value, 0);
	return (
		<div className="max-w-xs rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
			<p className="mb-1 font-medium text-popover-foreground">{label}</p>
			{shown.map((p) => (
				<p key={p.name} className="flex items-center gap-2">
					<span
						className="inline-block size-2 shrink-0 rounded-full"
						style={{ background: p.color }}
					/>
					<span className="flex-1 truncate text-muted-foreground">
						{p.name}
					</span>
					<span className="tabular-nums text-popover-foreground">
						{p.value.toFixed(1)}h
					</span>
					<span className="w-9 text-right tabular-nums text-muted-foreground">
						{total > 0 ? Math.round((p.value / total) * 100) : 0}%
					</span>
				</p>
			))}
		</div>
	);
}

export default function RatiosChart({ matters, limit = 15 }: RatiosChartProps) {
	const shown = matters.slice(0, limit);
	if (shown.length === 0) return null;

	// The busiest timekeepers across the shown matters get their own colour;
	// everyone else is pooled so the legend stays legible.
	const totals = new Map<string, number>();
	for (const m of shown) {
		for (const c of m.contributors) {
			totals.set(c.profName, (totals.get(c.profName) ?? 0) + c.hours);
		}
	}
	const named = [...totals.entries()]
		.sort((a, b) => b[1] - a[1])
		.slice(0, MAX_SERIES)
		.map(([name]) => name);
	const namedSet = new Set(named);
	const hasOther = totals.size > named.length;

	const data = shown.map((m) => {
		const row: Record<string, string | number> = {
			label: truncate(m.matterDescription?.trim() || m.matterNumber, 32),
		};
		for (const key of named) row[key] = 0;
		if (hasOther) row[OTHER_KEY] = 0;
		for (const c of m.contributors) {
			const key = namedSet.has(c.profName) ? c.profName : OTHER_KEY;
			row[key] = ((row[key] as number) ?? 0) + c.hours;
		}
		return row;
	});

	const series = hasOther ? [...named, OTHER_KEY] : named;
	const height = Math.max(200, shown.length * 30 + 40);

	return (
		<div className="flex flex-col gap-3">
			<div className="w-full" style={{ height }}>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={data}
						layout="vertical"
						margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
						barCategoryGap="22%"
					>
						<CartesianGrid
							strokeDasharray="3 3"
							horizontal={false}
							stroke="var(--border)"
						/>
						<XAxis
							type="number"
							tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
							tickLine={false}
							axisLine={{ stroke: "var(--border)" }}
							unit="h"
						/>
						<YAxis
							type="category"
							dataKey="label"
							tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
							tickLine={false}
							axisLine={false}
							width={220}
						/>
						<Tooltip
							content={<ChartTooltip />}
							cursor={{ fill: "var(--muted)", opacity: 0.4 }}
						/>
						{series.map((name, i) => (
							<Bar
								key={name}
								dataKey={name}
								stackId="hours"
								fill={name === OTHER_KEY ? OTHER_COLOR : SERIES_COLORS[i]}
								radius={i === series.length - 1 ? [0, 3, 3, 0] : undefined}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</div>

			<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
				{series.map((name, i) => (
					<span key={name} className="flex items-center gap-1.5">
						<span
							className="inline-block size-2 rounded-full"
							style={{
								background: name === OTHER_KEY ? OTHER_COLOR : SERIES_COLORS[i],
							}}
						/>
						{name}
					</span>
				))}
			</div>
		</div>
	);
}
